
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { testExists, getFormattedPoint } from "@/utils/level2/testUtils";
import TestSelector from "@/components/level2/TestSelector";
import TestInterface from "@/components/level2/TestInterface";
import TrainingHeader from "@/components/level2/TrainingHeader";
import { useLevel2Training } from "@/hooks/level2/useLevel2Training";
import { hasSavedExerciseData } from "@/hooks/level3/useAutoSave";
import { 
  puttingTests,
  chippingTests,
  wedgingTests,
  bunkerTests,
  ironTests,
  fairwayWoodTests,
  driverTests,
  levels
} from "@/data/level2/tests";
import { getTableHeadTitle } from "@/utils/level3/titles";
import { getTestPoints, getTestDistances } from "@/services/level2";
import NavigationMenu from "@/components/layout/NavigationMenu";

const Level2Training = () => {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  
  const {
    scores,
    ballTendencies,
    selectedTest,
    currentPoint,
    currentAttempt,
    handleScoreChange,
    cancelLastEntry,
    handleFinish,
    handleSelectTest,
    handleBackToTests,
    getPointTotal,
    getGrandTotal
  } = useLevel2Training();

  // Handle test selection including validation
  const handleTestSelection = (testId: string) => {
    if (testExists(testId)) {
      handleSelectTest(testId);
    }
  };
  
  // Ne charger depuis l'URL que s'il n'y a pas de données sauvegardées
  React.useEffect(() => {
    const testParam = searchParams.get('test');
    const { hasData } = hasSavedExerciseData();
    
    // Si des données sont sauvegardées, ne pas interférer
    if (hasData) {
      console.log('Données sauvegardées détectées, restauration en cours...');
      return;
    }
    
    if (testParam && !selectedTest && testExists(testParam)) {
      handleSelectTest(testParam);
    }
  }, [searchParams, selectedTest, handleSelectTest]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      <div className="container mx-auto py-8">
        <TrainingHeader />

        {selectedTest === null ? (
          <TestSelector 
            key={i18n.language}
            puttingTests={puttingTests}
            chippingTests={chippingTests}
            wedgingTests={wedgingTests}
            bunkerTests={bunkerTests}
            ironTests={ironTests}
            fairwayWoodTests={fairwayWoodTests}
            driverTests={driverTests}
            onSelectTest={handleTestSelection}
          />
        ) : (
          <TestInterface
            key={i18n.language}
            selectedTest={selectedTest}
            currentPoint={currentPoint}
            currentAttempt={currentAttempt}
            scores={scores}
            onBackToTests={handleBackToTests}
            onFinish={handleFinish}
            onScoreChange={handleScoreChange}
            onCancelLastEntry={cancelLastEntry}
            getTestPoints={getTestPoints}
            getTestDistances={getTestDistances}
            getTableHeadTitle={() => getTableHeadTitle(selectedTest)}
            getFormattedPoint={(point) => getFormattedPoint(point, selectedTest)}
            getPointTotal={getPointTotal}
            getGrandTotal={getGrandTotal}
            levels={levels}
          />
        )}
      </div>
    </div>
  );
};

export default Level2Training;
